import { z } from 'zod'
import { defineSystemEvent } from './definition.js'
import {
  systemEventChangedFieldsSchema,
  systemEventChangesSchema,
  systemEventFailureDataSchema,
} from './primitives.js'

const roleData = z.strictObject({ role_id: z.uuid(), name: z.string().optional() })
const roleTarget = roleData.pick({ role_id: true })
const userData = z.strictObject({ user_id: z.uuid() })
const membershipData = z.strictObject({ membership_id: z.uuid() })
/** Why a membership was created or changed, when it wasn't a plain edit. */
const membershipChangeVia = z.enum([
  'INVITE',
  'INVITE_ACCEPTED',
  'REINVITE',
  'INVITE_CANCELLED',
  'TRANSFER',
  'GLOBAL_ACCESS',
])
export type SystemEventMembershipChangeVia = z.infer<typeof membershipChangeVia>
const organizationData = z.strictObject({ organization_id: z.uuid(), name: z.string().optional() })
const organizationTarget = organizationData.pick({ organization_id: true })

export const identitySystemEvents = {
  roleCreateSuccess: defineSystemEvent({
    type: 'CREATE_ROLE',
    status: 'SUCCESS',
    entityTypes: ['ROLE'],
    data: roleData,
  }),
  roleCreateFailed: defineSystemEvent({
    type: 'CREATE_ROLE',
    status: 'FAIL',
    entityTypes: ['ROLE'],
    data: systemEventFailureDataSchema,
  }),
  roleUpdateSuccess: defineSystemEvent({
    type: 'UPDATE_ROLE',
    status: 'SUCCESS',
    entityTypes: ['ROLE'],
    data: roleTarget.extend({ changes: systemEventChangesSchema }),
  }),
  roleUpdateFailed: defineSystemEvent({
    type: 'UPDATE_ROLE',
    status: 'FAIL',
    entityTypes: ['ROLE'],
    data: roleTarget.extend(systemEventFailureDataSchema.shape),
  }),
  roleDeleteSuccess: defineSystemEvent({
    type: 'DELETE_ROLE',
    status: 'SUCCESS',
    entityTypes: ['ROLE'],
    data: roleData,
  }),
  roleDeleteFailed: defineSystemEvent({
    type: 'DELETE_ROLE',
    status: 'FAIL',
    entityTypes: ['ROLE'],
    data: roleTarget.extend(systemEventFailureDataSchema.shape),
  }),
  userCreateSuccess: defineSystemEvent({
    type: 'CREATE_USER',
    status: 'SUCCESS',
    entityTypes: ['USER'],
    data: userData,
  }),
  userCreateFailed: defineSystemEvent({
    type: 'CREATE_USER',
    status: 'FAIL',
    entityTypes: ['USER'],
    data: systemEventFailureDataSchema,
  }),
  /**
   * UPDATE_USER is keyed by the User ID, except for these fields, which live on Membership.
   * Their events carry the membership ID as the entity and user_id until they're cleaned up:
   * - roles (updateUserRoles)
   * - policiesAcceptedAt (acceptTermsAndConditions)
   * - profilePictureId (updateUserProfilePicture)
   */
  userUpdateSuccess: defineSystemEvent({
    type: 'UPDATE_USER',
    status: 'SUCCESS',
    entityTypes: ['USER'],
    // Record which fields changed; do not accept an unfiltered user/authentication snapshot.
    data: userData.extend({ changed_fields: systemEventChangedFieldsSchema }),
  }),
  userUpdateFailed: defineSystemEvent({
    type: 'UPDATE_USER',
    status: 'FAIL',
    entityTypes: ['USER'],
    data: userData.extend(systemEventFailureDataSchema.shape),
  }),
  // A MEMBERSHIP is a person's seat in one organization; a USER is the person across all of them.
  membershipCreateSuccess: defineSystemEvent({
    type: 'CREATE_MEMBERSHIP',
    status: 'SUCCESS',
    entityTypes: ['MEMBERSHIP'],
    data: membershipData.extend({ via: membershipChangeVia }),
  }),
  membershipCreateFailed: defineSystemEvent({
    type: 'CREATE_MEMBERSHIP',
    status: 'FAIL',
    entityTypes: ['MEMBERSHIP'],
    data: systemEventFailureDataSchema,
  }),
  membershipUpdateSuccess: defineSystemEvent({
    type: 'UPDATE_MEMBERSHIP',
    status: 'SUCCESS',
    entityTypes: ['MEMBERSHIP'],
    data: membershipData.extend({
      changed_fields: systemEventChangedFieldsSchema,
      via: membershipChangeVia.optional(),
    }),
  }),
  membershipUpdateFailed: defineSystemEvent({
    type: 'UPDATE_MEMBERSHIP',
    status: 'FAIL',
    entityTypes: ['MEMBERSHIP'],
    data: membershipData.extend(systemEventFailureDataSchema.shape),
  }),
  userDeleteSuccess: defineSystemEvent({
    type: 'DELETE_USER',
    status: 'SUCCESS',
    entityTypes: ['USER'],
    data: userData,
  }),
  userDeleteFailed: defineSystemEvent({
    type: 'DELETE_USER',
    status: 'FAIL',
    entityTypes: ['USER'],
    data: userData.extend(systemEventFailureDataSchema.shape),
  }),
  userLoginSuccess: defineSystemEvent({
    type: 'LOGIN_USER',
    status: 'SUCCESS',
    entityTypes: ['USER'],
    // ORG_SWITCH: an existing session entered this organization, without a fresh sign-in
    data: userData.extend({ via: z.enum(['ORG_SWITCH']).optional() }),
  }),
  userLoginFailed: defineSystemEvent({
    type: 'LOGIN_USER',
    status: 'FAIL',
    entityTypes: ['USER'],
    // Authentication may fail before a user ID is known. Tenant attribution is still required.
    data: userData.partial().extend(systemEventFailureDataSchema.shape),
  }),
  userLogoutSuccess: defineSystemEvent({
    type: 'LOGOUT_USER',
    status: 'SUCCESS',
    entityTypes: ['USER'],
    data: userData,
  }),
  userLogoutFailed: defineSystemEvent({
    type: 'LOGOUT_USER',
    status: 'FAIL',
    entityTypes: ['USER'],
    data: userData.extend(systemEventFailureDataSchema.shape),
  }),
  userPasswordChangeSuccess: defineSystemEvent({
    type: 'CHANGE_USER_PASSWORD',
    status: 'SUCCESS',
    entityTypes: ['USER'],
    data: userData,
  }),
  userPasswordChangeFailed: defineSystemEvent({
    type: 'CHANGE_USER_PASSWORD',
    status: 'FAIL',
    entityTypes: ['USER'],
    data: userData.extend(systemEventFailureDataSchema.shape),
  }),
  // Creation failures before a tenant exists remain internal diagnostics.
  organizationCreateSuccess: defineSystemEvent({
    type: 'CREATE_ORGANIZATION',
    status: 'SUCCESS',
    entityTypes: ['ORGANIZATION'],
    data: organizationData,
  }),
  organizationUpdateSuccess: defineSystemEvent({
    type: 'UPDATE_ORGANIZATION',
    status: 'SUCCESS',
    entityTypes: ['ORGANIZATION'],
    data: organizationTarget.extend({ changed_fields: systemEventChangedFieldsSchema }),
  }),
  organizationUpdateFailed: defineSystemEvent({
    type: 'UPDATE_ORGANIZATION',
    status: 'FAIL',
    entityTypes: ['ORGANIZATION'],
    data: organizationTarget.extend(systemEventFailureDataSchema.shape),
  }),
  organizationDeleteSuccess: defineSystemEvent({
    type: 'DELETE_ORGANIZATION',
    status: 'SUCCESS',
    entityTypes: ['ORGANIZATION'],
    data: organizationData,
  }),
  organizationDeleteFailed: defineSystemEvent({
    type: 'DELETE_ORGANIZATION',
    status: 'FAIL',
    entityTypes: ['ORGANIZATION'],
    data: organizationTarget.extend(systemEventFailureDataSchema.shape),
  }),
}
